import React, { Component } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';

// Bounds for the check-number field. Production held a 78-character "check number"
// that was really a remittance note — the field was doing duty as the notes field
// that did not exist. Real check numbers are short; ACH references run longer.
const CHECK_NUMBER_MAX = 30;
const NOTES_MAX = 255;

export class BatchCheckPayment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      poNumber: '',
      checkNumber: '',
      // Deliberately NO default. This date is read off a physical check on a
      // reconciliation screen; defaulting it to today is how batch 2381757 was
      // stored a day late — the field was never touched.
      checkDate: '',
      notes: '',
      isSubmitting: false
    };
  }

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const { poNumber, checkNumber, checkDate, notes } = this.state;
    const { selectedInvoices, onSubmit, mode } = this.props;
    const isLink = mode === 'link';

    if (isLink) {
      if (!poNumber.trim()) {
        alert('Please enter a PO number');
        return;
      }
    } else {
      if (!checkNumber.trim()) {
        alert('Please enter a check number');
        return;
      }
      if (!checkDate) {
        alert('Please enter a check date');
        return;
      }
    }

    try {
      this.setState({ isSubmitting: true });
      if (isLink) {
        await onSubmit(selectedInvoices, poNumber, '', '');
      } else {
        await onSubmit(selectedInvoices, checkNumber.trim(), checkDate, notes.trim());
      }
    } catch (error) {
      console.error("Error processing batch:", error);
      alert(isLink ? 'Error linking invoices. Please try again.' : 'Error processing check payment. Please try again.');
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  render() {
    const { show, onClose, selectedInvoices, mode } = this.props;
    const { poNumber, checkNumber, checkDate, notes, isSubmitting } = this.state;
    const isLink = mode === 'link';

    const totalAmount = selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);

    return (
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        aria-labelledby="batch-check-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="batch-check-modal">
            {isLink ? 'Batch Assign PO' : 'Process Check Payment'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="batch-check-summary">
            <h5>Selected Invoices ({selectedInvoices.length})</h5>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>{isLink ? 'Specialist' : 'Dealer'}</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoices.map((invoice) => (
                    <tr key={invoice.invoiceId}>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{invoice.name}</td>
                      <td>{this.formatCurrency(parseFloat(invoice.amount))}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px'
            }}>
              Total Amount: {this.formatCurrency(totalAmount)}
            </div>

            <Form onSubmit={this.handleSubmit}>
              {isLink ? (
                <Form.Group controlId="poNumber" className="mb-3">
                  <Form.Label>PO Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="poNumber"
                    value={poNumber}
                    onChange={this.handleChange}
                    placeholder="Enter PO number"
                    autoFocus
                  />
                  <Form.Text className="text-muted">
                    This PO number will be recorded for all selected invoices
                  </Form.Text>
                </Form.Group>
              ) : (
                <>
                  <Form.Group controlId="checkNumber" className="mb-3">
                    <Form.Label>Check Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="checkNumber"
                      value={checkNumber}
                      onChange={this.handleChange}
                      placeholder="Enter check number from dealer"
                      maxLength={CHECK_NUMBER_MAX}
                      required
                      autoFocus
                    />
                    <Form.Text className="text-muted">
                      The check or ACH reference only — anything else belongs in Notes below
                    </Form.Text>
                  </Form.Group>
                  <Form.Group controlId="checkDate" className="mb-3">
                    <Form.Label>Check Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="checkDate"
                      value={checkDate}
                      onChange={this.handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="notes" className="mb-3">
                    <Form.Label>Notes <span className="text-muted">(optional)</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={notes}
                      onChange={this.handleChange}
                      maxLength={NOTES_MAX}
                      placeholder="e.g. remittance covers invoices not in Jobvious"
                    />
                  </Form.Group>
                  <Form.Text className="text-muted">
                    This check number, date and note will be recorded for all selected invoices
                  </Form.Text>
                </>
              )}
            </Form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : (isLink ? "Assign PO" : "Process Check Payment")}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
